import { useState, useEffect, useMemo, useCallback } from "react";
import { getPricePerKg, getOriginalPricePerKg, validateKg } from "../utils/getPricingForKg";

const useCategoryForm = (categories, pricingData, requestType) => {
  const buildInitialState = useCallback(
    (cats) =>
      cats.reduce(
        (acc, cat) => ({
          ...acc,
          [cat.id]: {
            kg: "",
            deduction: "",
            manualPrice: "",
            isManualMode: false,
            showDeduction: false,
            kgError: null,
          },
        }),
        {}
      ),
    []
  );

  const [categoryData, setCategoryData] = useState({});

  useEffect(() => {
    if (categories.length > 0) {
      setCategoryData(buildInitialState(categories));
    }
  }, [categories, buildInitialState]);

  // ─── Generic field updater ────────────────────────────────────────────────
  const updateCategory = useCallback((categoryId, field, value) => {
    setCategoryData((prev) => ({
      ...prev,
      [categoryId]: {
        ...prev[categoryId],
        [field]: value,
      },
    }));
  }, []);

  // ─── KG change handler — validates range on every kg change ──────────────
  const updateKg = useCallback(
    (categoryId, kg) => {
      const cat = categories.find((c) => c.id === categoryId);
      const error = cat
        ? validateKg(pricingData, cat.pricingKey, kg, requestType)
        : null;

      setCategoryData((prev) => ({
        ...prev,
        [categoryId]: {
          ...prev[categoryId],
          kg,
          kgError: error,
        },
      }));
    },
    [categories, pricingData, requestType]
  );

  // ─── Computed amounts with tiered pricing ─────────────────────────────────
  const computedAmounts = useMemo(() => {
    return categories.reduce((acc, cat) => {
      const data = categoryData[cat.id];
      if (!data) return acc;

      const kg = Number(data.kg) || 0;
      const deductionPct = Number(data.deduction) || 0;

      // Original price_per_kg × kg = Amount in Naira
      const originalPricePerKg = data.isManualMode
        ? Number(data.manualPrice) || 0
        : getOriginalPricePerKg(pricingData, cat.pricingKey, kg, requestType);

      // discount_percentage — flat % value from tier, not a calculation
      const discountPercentage = data.isManualMode
        ? 0
        : getPricePerKg(pricingData, cat.pricingKey, kg, requestType);

      const amountInNaira = originalPricePerKg * kg;

      // Legacy deduction calculation (kept for payload compatibility)
      const deductionAmount = amountInNaira * (deductionPct / 100);
      const finalAmount = amountInNaira - deductionAmount;

      return {
        ...acc,
        [cat.id]: {
          originalPricePerKg,
          discountPercentage,
          amountInNaira: Math.round((amountInNaira + Number.EPSILON) * 100) / 100,
          deductionAmount: Math.round((deductionAmount + Number.EPSILON) * 100) / 100,
          finalAmount: Math.round((finalAmount + Number.EPSILON) * 100) / 100,
        },
      };
    }, {});
  }, [categoryData, categories, pricingData, requestType]);

  // ─── Totals — only sum categories with valid kg and no errors ─────────────
  const totals = useMemo(() => {
    const values = Object.entries(computedAmounts)
      .filter(([id]) => {
        const data = categoryData[id];
        return data && Number(data.kg) > 0 && !data.kgError;
      })
      .map(([, v]) => v);

    // totalAmount sums amountInNaira (price_per_kg × kg) across valid categories
    const totalAmount = values.reduce((sum, v) => sum + (v.amountInNaira || 0), 0);

    // totalDeduction kept for payload compatibility but will be 0 since % is commented out
    const totalDeduction = values.reduce(
      (sum, v) => sum + (v.deductionAmount || 0),
      0
    );

    return {
      totalAmount: Math.round((totalAmount + Number.EPSILON) * 100) / 100,
      totalDeduction: Math.round((totalDeduction + Number.EPSILON) * 100) / 100,
    };
  }, [computedAmounts, categoryData]);

  // ─── Whether the form has any validation errors ───────────────────────────
  const hasErrors = useMemo(() => {
    return Object.values(categoryData).some((data) => !!data.kgError);
  }, [categoryData]);

  // ─── Build payload — excludes categories with errors or empty kg ──────────
  const buildWastePayload = useCallback(() => {
    return categories
      .filter((cat) => {
        const data = categoryData[cat.id];
        return data && Number(data.kg) > 0 && !data.kgError;
      })
      .map((cat) => {
        const data = categoryData[cat.id];
        return {
          name: cat.pricingKey,
          kg: Number(data.kg),
          deduction: data.deduction
            ? Number(data.deduction).toFixed(2)
            : "0.00",
        };
      });
  }, [categories, categoryData]);

  // ─── Reset ────────────────────────────────────────────────────────────────
  const resetForm = useCallback(() => {
    setCategoryData(buildInitialState(categories));
  }, [categories, buildInitialState]);

  return {
    categoryData,
    updateCategory,
    updateKg,
    computedAmounts,
    totals,
    hasErrors,
    buildWastePayload,
    resetForm,
  };
};

export default useCategoryForm;
